<template>
  <section class="rest-workspace">
    <!-- 顶栏操作和过滤框 -->
    <table-toolbar
      :search.sync="pageKeyword"
      :show-search="true"
      :show-refresh="true"
      @refresh="handleReset"
      :show-column-settings="true"
      :columns="columnConfigOptions"
      :visible-columns.sync="optionalVisibleColumns"
      :default-columns="columnConfigOptions.map(v => v.prop)"
      :fixed-columns="restFixedColumnsV2"
      :min-selected="4"
    >
      <template slot="left">
        <el-button type="primary" size="small" icon="el-icon-plus" :disabled="!canCreate" @click="dialogFormAdd" class="btn-action" style="order: 1;">{{ createLabel }}</el-button>

        <span v-if="(canCreate || canUpdate || canDelete) && !remote.realtime" class="rest-reload-group" :style="{ order: filtersBeforeReload ? 3 : 2 }">
          <el-popconfirm icon="el-icon-warning" :title="`确定要立即${reloadLabel}吗？该操作会调用后端重载接口。`" @confirm="reload">
            <el-button slot="reference" type="warning" size="small" icon="el-icon-warning-outline" class="btn-reload">{{ reloadLabel }}</el-button>
          </el-popconfirm>
          <span v-if="needReload" style="margin-left: 10px; color: #ef4444; font-size: 13px; font-weight: 500;">👈 发现你修改了数据，点击重载才会生效！</span>
        </span>

        <el-form v-if="search.pathname" ref="searchForm" :inline="true" size="small" class="search-form-inline" :style="{ marginBottom: 0, order: filtersBeforeReload ? 2 : 3 }">
          <template>
            <el-form-item v-for="(value, key) in visibleSearchParams" :key="key" class="filter-form-item" style="margin-bottom: 0;">
              <template v-if="searchFormTemplate[key] && searchFormTemplate[key].type === 'select'">
                <el-select v-model="search.params[key]" clearable :placeholder="searchPlaceholder(key)" @change="handleCurrentChange(1)" class="filter-select">
                  <el-option v-for="item in searchFormTemplate[key].options" :key="item.value" :label="item.value + ' - ' + item.label" :value="item.value"/>
                </el-select>
              </template>
              <template v-else>
                <el-input v-model="search.params[key]" @input="change()" :placeholder="searchPlaceholder(key)" clearable class="server-search-input">
                  <i slot="suffix" class="el-icon-set-up" style="line-height: 32px; color: #94a3b8; font-size: 14px; margin-right: 4px;"></i>
                </el-input>
              </template>
            </el-form-item>
            <slot name="formExt1"></slot>
          </template>
        </el-form>

        <!-- 导入功能（导出统一放在右侧图标区） -->
        <el-button v-if="importable" type="info" size="small" @click="importVisible = true;" class="btn-import" style="margin-left: 8px; order: 4;">导入数据</el-button>

        <span style="order: 5;"><slot name="formExt2"></slot></span>
      </template>

      <template v-if="exportable && exportVisible" slot="export">
        <el-dropdown trigger="click" @command="handleExportCommand">
          <el-tooltip content="导出数据" placement="top" :enterable="false" :open-delay="500">
            <el-button size="small" :loading="exportExcelLoading" icon="el-icon-download" class="btn-toolbar-icon"></el-button>
          </el-tooltip>
          <el-dropdown-menu slot="dropdown">
            <el-dropdown-item command="current"><i class="el-icon-document"></i> 导出本页</el-dropdown-item>
            <el-dropdown-item command="all"><i class="el-icon-folder-opened"></i> 导出全部</el-dropdown-item>
          </el-dropdown-menu>
        </el-dropdown>
      </template>
    </table-toolbar>

    <!-- 表格主体 -->
    <table-page
      :loading="tableLoading"
      :empty="displayTableData.length === 0"
      :current-page.sync="currentPage"
      :page-size.sync="response.page.size"
      :total="response.page.totalElements || 0"
      :page-sizes="[10, 20, 50, 100, 200, 1000]"
      :show-top-pagination="!!response.page.totalElements"
      :show-bottom-pagination="!!response.page.totalElements"
      @size-change="handleSizeChange"
      @current-change="handleCurrentChange"
      @retry="fetchData"
    >
      <el-table :data="displayTableData" stripe class="premium-table"
        :header-cell-class-name="handleHeaderCellClassName" tooltip-effect="dark" @sort-change="handleSortChange">
        <el-table-column
          v-for="(item, index) in tableColumnsForTable"
          :key="'col-' + item.prop + '-' + index"
          :label="tableColumnLabel(item)"
          :prop="item.prop"
          :min-width="item.minWidth"
          :fixed="item.fixed"
          :sortable="item.unSortable ? false : 'custom'"
          :show-overflow-tooltip="!!item.showOverflowTooltip"
        >
          <template slot="header">
            <span v-if="item.prop === 'id'" class="premium-header-title"><span class="header-icon-hash">#</span> ID</span>
            <span v-else>{{ tableColumnLabel(item) }}<sup style="color:#FF6347">
              {{ (v = sortParams.findIndex((x) => x.prop === item.prop)) >= 0 ? v + 1: '' }}
            </sup></span>
          </template>
          <template v-slot="scope">
            <template v-if="$scopedSlots[`tableColumn-${item.prop}`]">
              <slot :name="`tableColumn-${item.prop}`" :item="item" :row="scope.row"></slot>
            </template>
            <span v-else-if="item.prop === 'id'" class="cell-id" v-html="utils.tableFormatter(item, scope.row[item.prop], scope.row)" />
            <ext-text-popover v-else-if="(item.tableType === 'extTextPopover' || item.type === 'extTextPopover' || utils.isTextTypeTooLong(item.type, scope.row[item.prop])) && !item.showOverflowTooltip" :content="scope.row[item.prop]" />
            <template v-else-if="item.type === 'slot'">
              <slot :name="`tableColumn-${item.prop}`" :item="item" :row="scope.row"></slot>
            </template>
            <span v-else v-html="utils.tableFormatter(item, scope.row[item.prop], scope.row)" />
          </template>
        </el-table-column>

        <el-table-column key="col-actions" label="操作" width="80" align="center">
          <template v-slot="scope">
            <el-dropdown @command="handleActionCommand($event, scope.row, scope.$index)" trigger="hover">
              <el-button size="mini" class="action-btn" circle icon="el-icon-more-outline"></el-button>
              <el-dropdown-menu slot="dropdown" class="premium-dropdown-menu">
                <el-dropdown-item command="edit" :disabled="!canUpdate">
                  <i class="el-icon-edit"></i> 编辑数据
                </el-dropdown-item>
                <el-dropdown-item command="delete" :disabled="!canDelete" style="color: #ef4444;">
                  <i class="el-icon-delete" style="color: #ef4444;"></i> 删除数据
                </el-dropdown-item>
              </el-dropdown-menu>
            </el-dropdown>
          </template>
        </el-table-column>
      </el-table>
    </table-page>

    <!-- 新增/编辑容器：按业务页面配置 Drawer，其余页面保留原 Dialog -->
    <component
      :is="useDrawer ? 'el-drawer' : 'el-dialog'"
      :title="formTitle"
      :visible.sync="dialogVisible"
      :direction="useDrawer ? 'rtl' : undefined"
      :size="useDrawer ? drawerSize : undefined"
      :with-header="!useDrawer"
      :custom-class="useDrawer ? 'rest-form-drawer' : ''"
      :fullscreen="isDialogFullscreen"
      :width="dialogWidth"
      :top="dialogTop"
      :class="useDrawer ? '' : 'premium-dialog'"
      append-to-body
      destroy-on-close
      center
    >
      <div v-if="useDrawer" class="drawer-header">
        <div class="drawer-title-wrap">
          <span class="drawer-title-icon"><i :class="tableDataIndex < 0 ? 'el-icon-plus' : 'el-icon-edit-outline'"></i></span>
          <span class="drawer-title">{{ formTitle }}</span>
          <div class="drawer-header-actions">
            <el-button size="small" icon="el-icon-back" :disabled="saveLoading" @click="dialogVisible = false">返回列表</el-button>
            <el-button size="small" type="success" :loading="saveLoading" :disabled="saveLoading" @click="dialogFormSubmit(false)">保存</el-button>
          </div>
        </div>
        <el-button size="small" icon="el-icon-close" class="drawer-close-btn" aria-label="关闭" @click="dialogVisible = false"></el-button>
      </div>

      <div :class="useDrawer ? 'drawer-content' : ''">
      <el-form
        ref="ruleForm"
        :model="formModel"
        :rules="formRules"
        :label-position="useDrawer ? 'right' : 'top'"
        :label-width="useDrawer ? '150px' : undefined"
        size="small"
        :class="['rest-premium-form', { 'drawer-horizontal-form': useDrawer }]"
      >
        <el-form-item
          v-for="(item, i) in tableColumnsForForm"
          :key="i"
          :label="item.label"
          :label-width="item.hideFormLabel ? '0px' : undefined"
          :prop="item.prop"
          :rules="item.rules ? item.rules(formModel) : null"
          :class="{
            'is-full-width': item.type === 'textarea' || item.type === 'productArgs' || item.type === 'appleProducts' || item.type === 'productTemplate' || item.prop === 'data',
            'is-label-hidden': item.hideFormLabel
          }"
        >
          <span v-if="!item.hideFormLabel" slot="label">
            {{ item.label }}
            <el-tooltip v-if="item.labelHelp" :content="item.labelHelp">
              <i class="el-icon-question" style="color: #94a3b8; margin-left: 2px;" />
            </el-tooltip>
          </span>
          <template v-if="item.type === 'switch'">
            <el-switch v-model="formModel[item.prop]" active-color="#10b981" inactive-color="#ef4444" :active-value="item.activeValue" :inactive-value="item.inactiveValue"/>
          </template>
          <template v-else-if="item.type === 'color'">
            <el-color-picker v-model="formModel[item.prop]" />
          </template>
          <template v-else-if="item.type === 'select'">
            <el-select v-model="formModel[item.prop]" :multiple="item.multiple" :disabled="(item.disabled && tableDataIndex >= 0) || (appIdFilter && appIdFilterFields.includes(item.prop))" placeholder="请选择" clearable
              :allow-create="item.allowCreate" :filterable="item.filterable || item.allowCreate" style="width: 100%;" @change="item.change ? item.change(formModel) : ''">
              <el-option v-for="option in item.options" :key="option.value" :label="option.value + ' - ' + option.label" :value="option.value" :disabled="option.disabled"/>
            </el-select>
          </template>
          <template v-else-if="item.type === 'radio'">
            <el-radio-group v-model="formModel[item.prop]">
              <el-radio v-for="option in item.options" :key="option.value" :label="option.value">{{option.label}}</el-radio>
            </el-radio-group>
          </template>
          <template v-else-if="item.type === 'number'">
            <el-input-number v-model="formModel[item.prop]" :min="item.min || -Infinity" :max="item.max || Infinity" :step="item.step || 1" :label="item.label"
              style="width: 100%;" :disabled="(item.disabled && tableDataIndex >= 0) || (appIdFilter && appIdFilterFields.includes(item.prop))" @change="item.change ? item.change(formModel) : null">
            </el-input-number>
          </template>
          <template v-else-if="item.type === 'date'">
            <el-date-picker v-model="formModel[item.prop]" type="date" style="width: 100%;" :placeholder="item.placeholder || item.label"></el-date-picker>
          </template>
          <template v-else-if="item.type === 'datetime'">
            <el-date-picker v-model="formModel[item.prop]" type="datetime" style="width: 100%;" :placeholder="item.placeholder || item.label"></el-date-picker>
          </template>
          <template v-else-if="item.type === 'argsKvl'">
            <args-kvl :form="formModel" :prop="item.prop" />
          </template>
          <template v-else-if="item.type === 'productTemplate'">
            <product-template :form="formModel" :prop="item.prop" />
          </template>
          <template v-else-if="item.type === 'productArgs'">
            <product-args v-if="dialogVisible" :form="formModel" :prop="item.prop" />
          </template>
          <template v-else-if="item.type === 'appleProducts'">
            <apple-products v-if="dialogVisible" :form="formModel" :prop="item.prop" />
          </template>
          <template v-else>
            <template v-if="item.formType === 'slot'">
              <slot :name="`formItem-${item.prop}`" v-bind:item="item" v-bind:form="formModel"></slot>
            </template>
            <el-input v-else-if="!(formModel[item.prop] instanceof Array)" v-model="formModel[item.prop]" :placeholder="item.placeholder || item.label"
              :show-password="item.showPassword" :type="item.type" :autosize="item.autosize" :disabled="(item.disabled && tableDataIndex >= 0) || (appIdFilter && appIdFilterFields.includes(item.prop))"
              @change="item.change ? item.change(formModel) : null">
            </el-input>
          </template>
          <template v-if="item.plusType === 'image'">
            <el-image v-if="typeof formModel[item.prop] === 'string'" :src="formModel[item.prop]"/>
            <el-image v-else style="width: 100px; height: 100px" fit="contain" :src="require('@/assets/img/view-more.jpg')" :preview-src-list="formModel[item.prop]"/>
          </template>
        </el-form-item>
      </el-form>
      </div>

      <div v-if="useDrawer" class="drawer-footer">
        <el-button size="small" @click="dialogVisible = false">取 消</el-button>
        <el-button size="small" type="primary" :loading="saveLoading" :disabled="saveLoading" @click="dialogFormSubmit(false)" class="btn-save">保 存</el-button>
      </div>
      <div v-else slot="footer" class="dialog-footer" style="text-align: right; padding-top: 10px;">
        <slot name="formItem-actions" v-bind:form="formModel" :dataIndex="tableDataIndex" :updateFn="customUpdate">
          <el-button size="small" v-if="tableDataIndex < 0" type="default" @click="dialogFormReset">重 置</el-button>
          <el-button size="small" @click="dialogVisible = false">取 消</el-button>
          <el-button size="small" type="primary" :loading="saveLoading" :disabled="saveLoading" @click="dialogFormSubmit(false)" class="btn-save">确 定</el-button>
        </slot>
      </div>
    </component>

    <!-- 导入弹窗（样式美化） -->
    <el-dialog title="导入数据" :visible.sync="importVisible" center destroy-on-close class="premium-dialog">
      <el-upload ref="import" drag :limit="1" accept=".xlsx" action="" style="text-align: center;" :auto-upload="false">
        <i class="el-icon-upload" />
        <div class="el-upload__text">将文件拖到此处，或<em>点击上传</em></div>
        <div slot="tip" class="el-upload__tip" style="margin-top: 10px; color: var(--text-muted);">只能上传通过【导出表格】得到的表格，不要修改前 2 行，不要插入其他列！</div>
      </el-upload>
      <div slot="footer" class="dialog-footer" style="text-align: right; padding-top: 10px;">
        <el-popconfirm title="你真的真的确定么？" @confirm="handleImportExcel(true)">
          <el-button slot="reference" size="small" type="danger" :disabled="true">覆盖</el-button>
        </el-popconfirm>
        <el-button type="primary" size="small" style="margin-left: 10px;" :loading="importExcelLoading" @click="handleImportExcel(false)" class="btn-save">更新</el-button>
        <el-divider />
        <el-descriptions :column="1" size="small" style="text-align: left;">
          <el-descriptions-item label="更新">插入新数据或更新旧数据，不会影响不在表格内的数据</el-descriptions-item>
          <el-descriptions-item label="覆盖">清空所有旧数据，然后导入表格的数据（非常危险）</el-descriptions-item>
        </el-descriptions>
      </div>
    </el-dialog>
  </section>
</template>

<script>
import * as XLSX from 'xlsx'
import api from '@/api'
import utils from '@/common/utils'
import ArgsKvl from '../ArgsKvl/index.vue'
import ProductTemplate from '@/components/ProductTemplate/index.vue'
import ProductArgs from '@/components/ProductArgs/index.vue'
import ExtTextPopover from '@/components/ExtTextPopover/index.vue'
import AppleProducts from '@/components/AppleProducts/index.vue'
import TablePage from '../TablePage/index.vue'
import TableToolbar from '../TableToolbar/index.vue'

export default {
  name: 'Rest',
  components: { ArgsKvl, ProductTemplate, ProductArgs, ExtTextPopover, AppleProducts, TablePage, TableToolbar },
  props: {
    remote: { type: Object, required: true },
    tableColumns: { type: Array, required: true },
    formInitialize: { type: Object, required: true },
    formRules: { type: Object, required: true },
    listeners: { type: Object, default: null },
    tableFormatter: { type: Function, default: null },
    exportable: {
      type: Boolean,
      default: false
    },
    exportVisible: {
      type: Boolean,
      default: false
    },
    importable: {
      type: Boolean,
      default: false
    },
    createAble: {
      type: Boolean,
      default: false
    },
    updateAble: {
      type: Boolean,
      default: false
    },
    deleteAble: {
      type: Boolean,
      default: false
    },
    dialogFullscreen: {
      type: Boolean,
      default: false
    },
    dialogWidth: {
      type: String,
      default: '60%'
    },
    dialogTop: {
      type: String,
      default: '6vh'
    },
    appIdFilter: {
      type: Boolean,
      default: false
    },
    appIdFilterFields: {
      type: Array,
      default: () => ['appId', 'gameId']
    },
    useDrawer: {
      type: Boolean,
      default: false
    },
    drawerWidth: {
      type: String,
      default: '50%'
    },
    filtersBeforeReload: {
      type: Boolean,
      default: false
    }
  },

  data () {
    return {
      utils,
      response: { _embedded: {}, page: {}, _links: {} },
      search: { pathname: '', params: { } },
      needReload: false,
      dialogVisible: false,
      formModel: { ...this.formInitialize },
      tableDataIndex: -1,
      tableLoading: true,
      currentPage: 1, // 前端：页码从1开始, 后端：页码从0开始
      sortParams: [],
      exportExcelLoading: false,
      importExcelLoading: false,
      importVisible: false,
      saveLoading: false,
      canCreate: false,
      canUpdate: false,
      canDelete: false,
      pageKeyword: '',
      visibleColumns: this.tableColumns
        .filter((v) => !(v.hiddenTable || v.showPassword))
        .map((v) => v.prop)
    }
  },

  computed: {
    globalAppId () {
      return this.$store.getters.appId
    },
    visibleSearchParams () {
      if (!this.search || !this.search.params) return {}
      const filtered = {}
      Object.keys(this.search.params).forEach((key) => {
        if (!(this.appIdFilter && this.appIdFilterFields.includes(key))) {
          filtered[key] = this.search.params[key]
        }
      })
      return filtered
    },
    resource () {
      return utils.firstLowerCase(this.remote.resource)
    },
    resources () {
      return utils.pluralize(this.resource)
    },
    searchFormTemplate () {
      return this.tableColumns.reduce((pre, cur) => {
        return { ...pre, [cur.prop]: cur }
      }, {})
    },
    tableData () {
      if (this.listeners?.handleFetchData) {
        return this.listeners?.handleFetchData(this.response._embedded[this.resources])
      }
      return this.response._embedded[this.resources]
    },
    displayTableData () {
      const data = this.tableData || []
      const keyword = `${this.pageKeyword || ''}`.trim().toLowerCase()
      if (!keyword) {
        return data
      }
      return data.filter((row) => {
        return Object.keys(row).some((key) => {
          if (key === '_links') {
            return false
          }
          return String(row[key] ?? '').toLowerCase().indexOf(keyword) > -1
        })
      })
    },
    createPath () {
      return `/${this.remote.path}/admin/api/${this.resources}`
    },
    changePath () {
      return new URL(this.tableData[this.tableDataIndex]._links.self.href).pathname
    },
    profilePath () {
      return `/${this.remote.path}/admin/api/profile/${this.resources}`
    },
    reloadPath () {
      if (this.remote.reloadUrl) {
        return this.remote.reloadUrl
      }
      if (this.remote.reloadResource) {
        return `/${this.remote.path}/admin/reload/${this.remote.reloadResource}`
      }
      return `/${this.remote.path}/admin/reload/${this.resource}`
    },
    reloadLabel () {
      return this.remote.reloadLabel || '重载NACOS'
    },
    createLabel () {
      return this.remote.createLabel || '新增数据'
    },
    formTitle () {
      return this.tableDataIndex < 0 ? this.createLabel : '编辑数据'
    },
    drawerSize () {
      return this.isMobile ? '100%' : '50%'
    },
    restFixedColumns () {
      const columns = ['操作']
      if (this.columnConfigOptions.some(item => item.prop === 'id')) {
        columns.unshift('ID')
      }
      return columns
    },
    columnConfigOptions () {
      return this.tableColumns.filter((v) => !(v.hiddenTable || v.showPassword) && !this.isFixedTableColumn(v))
    },
    restFixedColumnsV2 () {
      return this.fixedTableColumns.map(item => this.tableColumnLabel(item)).concat('操作')
    },
    fixedTableColumns () {
      return this.tableColumns.filter((v) => !(v.hiddenTable || v.showPassword) && this.isFixedTableColumn(v))
    },
    optionalVisibleColumns: {
      get () {
        return this.visibleColumns.filter(prop => this.columnConfigOptions.some(item => item.prop === prop))
      },
      set (value) {
        this.visibleColumns = this.fixedTableColumns.map(item => item.prop).concat(value)
      }
    },
    tableColumnsForTable () {
      return this.tableColumns
        .filter((v) => !(v.hiddenTable || v.showPassword))
        .filter((v) => this.isFixedTableColumn(v) || this.visibleColumns.includes(v.prop))
    },
    tableColumnsForForm () {
      return this.tableColumns.filter((v) => !v.hiddenForm)
    },
    isMobile () {
      return this.$store.getters.isMobile
    },
    isDialogFullscreen () {
      if (this.isMobile) {
        return true
      }
      return this.dialogFullscreen
    }
  },
  watch: {
    dialogVisible (nVal) {
      if (!nVal && this.$refs.ruleForm) {
        this.$refs.ruleForm.resetFields()
      }
    },
    globalAppId (nVal) {
      if (this.appIdFilter) {
        this.applyAppIdFilter(nVal)
        this.fetchData()
      }
    }
  },
  created () {
    if (this.remote.sort) {
      this.sortParams = this.remote.sort.split(',').map((x) => {
        const items = x.split(':')
        return { prop: items[0], order: items.length > 1 ? items[1] : 'ascending' }
      })
    }

    const initiallyNeedSearch = this.appIdFilter
    this.fetchData(initiallyNeedSearch).then(() => {
      const { search } = this.response._links
      if (search) {
        api.get(new URL(search.href).pathname).then((response) => {
          const entry = Object.entries(response._links).filter((e) => e[0] !== 'self')[0]
          if (entry) {
            const data = entry[1]
            const hrefSplit = data.href.split(/({\?|})/)
            const params = hrefSplit[2].split(',')
            if (params.length > 3) {
              this.search.pathname = new URL(hrefSplit[0]).pathname
              Object.values(params).forEach((v) => {
                if (v !== 'page' && v !== 'size' && v !== 'sort') {
                  this.search.params[v] = ''
                }
              })
              if (this.appIdFilter) {
                this.applyAppIdFilter(this.globalAppId)
                this.fetchData()
                return
              }
            }
          }
          this.tableLoading = false
        }).catch(() => {
          this.tableLoading = false
        })
      } else {
        this.tableLoading = false
      }
    })

    if (this.importable || this.createAble || this.updateAble || this.deleteAble) {
      api.get(this.profilePath).then((response) => {
        Object.values(response.alps.descriptor).forEach((value) => {
          if (value.id) {
            const verb = value.id.split('-')[0]
            switch (verb) {
              case 'create': this.canCreate = this.createAble && true; break
              case 'update': this.canUpdate = this.updateAble && true; break
              case 'delete': this.canDelete = this.deleteAble && true; break
              default: break
            }
          }
        })
      })
    }
  },

  methods: {
    isFixedTableColumn (item) {
      return item.prop === 'id' || item.prop === 'name'
    },
    tableColumnLabel (item) {
      return item.prop === 'id' ? '# ID' : item.label
    },
    getFormValuesMethod () {
      return this.formModel
    },
    change () {
      this.$forceUpdate()
      this.handleCurrentChange(1)
    },

    handleReset () {
      this.fetchData()
    },

    tableColumn (prop) {
      return this.tableColumns.find((v) => v.prop === prop)
    },

    searchPlaceholder (prop) {
      const column = this.searchFormTemplate[prop]
      const label = column ? column.label : prop
      return column && column.type === 'select' ? `请选择${label}` : `请输入${label}`
    },

    fetchData (keepLoading = false) {
      this.tableLoading = true
      const useSearch = Object.values(this.search.params).find((x) => x)

      const params = []
      if (this.currentPage) {
        params.push(`page=${this.currentPage - 1}`)
      }
      if (this.response.page.size) {
        params.push(`size=${this.response.page.size}`)
      }

      if (this.sortParams) {
        this.sortParams.forEach((x) => {
          params.push(`sort=${x.prop},${x.order.replace('ending', '')}`)
        })
      }

      if (useSearch) {
        Object.keys(this.search.params).forEach((key) => {
          if (this.search.params[key]) {
            const val = encodeURIComponent(`${this.search.params[key]}`)
            params.push(`${key}=${val}`)
          }
        })
      }

      return api.get(`${useSearch ? this.search.pathname : this.createPath}?${params.join('&')}`)
        .then((response) => {
          this.response = response
          if (!keepLoading) {
            this.tableLoading = false
          }
        }).catch(() => {
          this.tableLoading = true
          this.$notify.error({
            title: '错误',
            message: '页面数据加载失败，请刷新页面再试'
          })
        })
    },

    reload () {
      api.get(this.reloadPath).then((res) => {
        const { code, msg } = res
        if (code !== 200) {
          this.$message.error(`${this.reloadLabel}失败，${code}: ${msg}`)
        } else {
          this.$message.success(`${this.reloadLabel}成功`)
        }
        this.needReload = false
      }).catch(() => {
        this.$message.error(`${this.reloadLabel}失败`)
      })
    },

    handleSizeChange (size) {
      this.response.page.size = size
      this.fetchData()
    },

    handleCurrentChange (number) {
      this.currentPage = number
      this.fetchData()
    },

    applyAppIdFilter (appId) {
      if (!this.appIdFilter) return
      for (const field of this.appIdFilterFields) {
        if (this.search && this.search.params && Object.hasOwn(this.search.params, field)) {
          this.search.params[field] = appId
        }
        if (this.formModel && Object.hasOwn(this.formModel, field)) {
          this.formModel[field] = appId
        }
      }
    },

    dialogFormAdd () {
      this.tableDataIndex = -1
      this.dialogFormReset()
      if (this.appIdFilter) {
        this.applyAppIdFilter(this.globalAppId)
      }
      this.dialogVisible = true
      this.formModel.tableDataIndex = -1
    },

    dialogFormReset () {
      if (this.$refs.ruleForm) {
        this.$refs.ruleForm.resetFields()
      }
      this.formModel = JSON.parse(JSON.stringify(this.formInitialize))
    },

    dialogFormEdit (row, index) {
      this.dialogVisible = true
      this.tableDataIndex = index
      this.formModel = JSON.parse(JSON.stringify({ ...this.formInitialize, ...row }))
      this.formModel.tableDataIndex = index
      this.tableColumns.forEach((column) => {
        if (column.prop === 'id' && !Object.hasOwn(row, 'id')) {
          if (row._links) {
            this.formModel.id = row._links.self.href.split('/').pop()
          }
        }
        if (column.codec === 'json') {
          this.formModel[column.prop] = JSON.parse(row[column.prop] || '[]')
        }
        if (column.multiple && this.formModel[column.prop]) {
          const isNumber = column.options[0] && typeof column.options[0].value === 'number'
          this.formModel[column.prop] = this.formModel[column.prop].split(',').map((x) => (isNumber ? Number(x) : x))
        }
      })
      if (this.listeners && this.listeners.beforeFormEdit) {
        this.listeners.beforeFormEdit(this.formModel)
      }
    },

    dialogFormSubmit (destroy) {
      this.$refs.ruleForm.validate((valid) => {
        if (valid) {
          if (!destroy) {
            this.saveLoading = true
          }
          let promise
          if (destroy) {
            promise = this.$confirm('是否永久删除该数据?', '提示', {
              confirmButtonText: '确定',
              cancelButtonText: '取消',
              type: 'warning'
            })
          } else {
            promise = new Promise((resolve) => {
              resolve()
            })
          }
          promise.then(() => {
            const create = this.tableDataIndex < 0
            let func = create ? api.post : api.put
            if (destroy) {
              func = api.delete
            }
            const pathname = create ? this.createPath : this.changePath
            const form = JSON.parse(JSON.stringify(this.formModel))
            for (const column of this.tableColumns) {
              const prop = column.prop
              if (prop === 'createdAt' || prop === 'updatedAt') {
                continue
              }
              const formVal = this.formModel[prop]
              if (!formVal) {
                continue
              }
              if (column.codec === 'json') {
                form[prop] = JSON.stringify(formVal)
              }
              if (column.multiple) {
                form[prop] = formVal.join(',')
              }
              if (column.type === 'date') {
                form[prop] = this.$moment(formVal).format(column.format || 'YYYY-MM-DD')
              } else if (column.type === 'datetime') {
                form[prop] = this.$moment(formVal).format(column.format || 'YYYY-MM-DDTHH:mm:ss.SSSZ')
              }
            }
            if (this.listeners && this.listeners.beforeFormSubmit) {
              this.listeners.beforeFormSubmit(form)
            }
            return func(pathname, form).then((response) => {
              this.dialogVisible = false
              this.needReload = true
              if (create || destroy) {
                this.$message.success(create ? '添加成功' : '删除成功')
                this.fetchData()
              } else {
                this.$message.success('修改成功')
                const responseData = this.listeners?.handleFetchData ? this.listeners.handleFetchData([response]) : [response]
                this.tableData.splice(this.tableDataIndex, 1, [...responseData].shift())
              }

              if (this.listeners && this.listeners.afterFormSubmit) {
                this.listeners.afterFormSubmit(this, create, form)
              }
            })
          }).catch((action) => {
            if (destroy && action === 'cancel') {
              this.$message({
                type: 'info',
                message: '已取消删除'
              })
            }
          }).finally(() => {
            this.saveLoading = false
          })
          return true
        }
        return false
      })
    },
    customUpdate () {
      this.dialogVisible = false
      this.fetchData()
    },

    rowOperate (row, index) {
      this.tableDataIndex = index
      const url = this.changePath.replace(this.resources, this.remote.operateUrlReplace)
      this.$notify.info('运行中...')
      api.get(url)
        .then((response) => {
          if (response.code === 0) {
            if (this.listeners && this.listeners.afterOperateSuccess) {
              this.listeners.afterOperateSuccess(this.$parent, response.result)
            } else {
              this.$notify.success(`成功：${response.result}`)
            }
          } else {
            this.$notify.error({ message: response.message, duration: 0 })
          }
        })
    },

    handleValueCopy (value) {
      navigator.clipboard.writeText(value).then(() => {
        this.$message.success('复制成功')
      })
    },

    clickExtra (row) {
      this.listeners.extra(this.$parent, row)
    },

    writeFile (currentPage, originalData) {
      const json = []
      originalData.forEach((data) => {
        const row = {
          id: data._links.self.href.split('/').pop()
        }
        Object.assign(row, data)
        delete (row.createdAt)
        delete (row.updatedAt)
        delete (row.fallbackType)
        delete (row.fullType)
        delete (row.links)
        delete (row._links)
        json.push(row)
      })
      if (json.length > 0) {
        const header = {}
        Object.keys(json[0]).forEach((x) => {
          const v = this.tableColumn(x)
          header[x] = v ? v.label : ''
        })
        header.id = '填0表示新增'
        json.splice(0, 0, header)
      }

      const workbook = XLSX.utils.book_new()
      const worksheet = XLSX.utils.json_to_sheet(json)
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Sheet1')

      const menus = this.$route.matched.filter((item) => item.path && item.name)
      const filename = `${menus.map((x) => x.name).join('-')}-${currentPage ? '部分' : '全部'}-${new Date().getTime()}.xlsx`
      XLSX.writeFile(workbook, filename)
    },

    handleDownload (currentPage) {
      if (currentPage) {
        this.writeFile(currentPage, this.tableData)
        return
      }

      this.exportExcelLoading = true

      const useSearch = Object.values(this.search.params).find((x) => x)
      const params = []
      if (useSearch) {
        Object.keys(this.search.params).forEach((key) => {
          if (this.search.params[key]) {
            const val = encodeURIComponent(`${this.search.params[key]}`)
            params.push(`${key}=${val}`)
          }
        })
      }
      params.push('size=1000')

      const path = this.createPath
      const { resources } = this

      const list = [];
      (async function () {
        let page = 0
        let totalPages = -1
        while (page !== totalPages) {
          const resp = await api.get(`${path}?${params.join('&')}&page=${page}`)
          list.push(...resp._embedded[resources])
          page += 1

          totalPages = resp.page.totalPages
          if (totalPages === -1) {
            break
          }
        }
      }()).then(() => {
        this.writeFile(currentPage, list)
        this.exportExcelLoading = false
      })
    },

    handleExportCommand (command) {
      this.handleDownload(command === 'current')
    },

    handleImportExcel (force) {
      if (force) {
        alert('暂未开放')
        return
      }
      this.importExcelLoading = true
      const file = this.$refs.import.uploadFiles ? this.$refs.import.uploadFiles[0] : null
      if (!file) {
        this.$message.error('你需要先选择文件！')
        return
      }
      const fileReader = new FileReader()
      fileReader.readAsArrayBuffer(file.raw)
      fileReader.onload = (e) => {
        const workbook = XLSX.read(e.target.result)
        if (workbook.SheetNames.length > 0) {
          const sheet = workbook.Sheets[workbook.SheetNames[0]]
          const rows = XLSX.utils.sheet_to_json(sheet, { defval: '' })
          rows.splice(0, 1)

          let countChange = 0
          let countIgnore = 0
          let countFail = 0
          const failMessages = []
          const notify = () => {
            if (countChange + countIgnore + countFail === rows.length) {
              this.importExcelLoading = false
              let info = `修改个数：${countChange}<br>未修改个数：${countIgnore}<br>`
              if (countFail > 0) {
                info += `失败个数：${countFail}<br>失败原因：<br>${failMessages.join('<br>')}<br>`
              }

              if (countChange > 0) {
                info += '<br>由于数据发生改变，页面即将刷新<br>如果确保数据没问题，记得手动点击《重载》按钮'
              }

              this.$alert(info, `导入个数：${rows.length}`, {
                confirmButtonText: '确定',
                dangerouslyUseHTMLString: true,
                callback: () => {
                  if (countChange > 0) {
                    this.$router.push({ path: '/empty' })
                  }
                }
              })
            }
          }

          const executing = new Set()

          rows.forEach(async (value) => {
            const now = new Date().getTime()
            let promise
            if (value.id) {
              promise = api.patch(`${this.createPath}/${value.id}`, value)
            } else {
              promise = api.post(this.createPath, value)
            }
            executing.add(promise)

            promise.then((resp) => {
              executing.delete(promise)
              if (now - new Date(resp.updatedAt).getTime() > 10000) {
                countIgnore += 1
              } else {
                countChange += 1
              }
              notify()
            }).catch(() => {
              executing.delete(promise)
              countFail += 1
              failMessages.push(`&nbsp;&nbsp;&nbsp;&nbsp;第${value.__rowNum__}行，ID=${value.id}`)
              notify()
            })

            if (executing.size > 5) {
              await Promise.race(executing)
            }
          })
          return
        }

        this.$message.error('文件处理异常')
      }
    },

    handleHeaderCellClassName ({ column }) {
      const param = this.sortParams.find((x) => x.prop === column.property)
      if (param) {
        column.order = param.order
      } else {
        column.order = ''
      }
    },

    handleSortChange ({ prop, order }) {
      const index = this.sortParams.findIndex((x) => x.prop === prop)
      if (index >= 0) {
        if (order) {
          this.sortParams[index].order = order
        } else {
          this.sortParams.splice(index, 1)
        }
      } else if (this.remote.singleSort) {
        this.sortParams = [{ prop, order }]
      } else {
        this.sortParams.push({ prop, order })
      }
      this.fetchData()
    },

    handleActionCommand (command, row, index) {
      if (command === 'edit') {
        this.dialogFormEdit(row, index)
      } else if (command === 'delete') {
        this.dialogFormEdit(row, index)
        this.$nextTick(() => {
          this.dialogFormSubmit(true)
        })
      }
    }
  }
}
</script>

<style scoped>
.rest-workspace {
  padding: 16px 24px;
}

/* 过滤筛选卡片 */
.filter-card {
  margin-top: 0 !important;
  margin-bottom: 20px;
  padding: 16px 24px;
  border-radius: var(--border-radius-card);
  background: var(--bg-card);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-base);
}

.flex-container {
  display: flex;
  justify-content: space-between;
  align-items: center;
  flex-wrap: wrap;
  gap: 16px;
}

.filter-left-group {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.filter-right-group {
  display: flex;
  align-items: center;
  gap: 12px;
}

.btn-action {
  flex-shrink: 0;
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
  border-radius: 8px !important;
  height: 32px;
  padding: 0 16px;
}

.btn-action:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.btn-toolbar-icon {
  width: 32px !important;
  height: 32px !important;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin: 0 !important;
  padding: 0 !important;
  border-color: var(--border-color) !important;
  border-radius: 8px !important;
  background-color: var(--bg-card) !important;
  color: var(--text-muted) !important;
}

.btn-toolbar-icon:hover,
.btn-toolbar-icon:focus {
  border-color: var(--border-hover) !important;
  background-color: var(--bg-page) !important;
  color: var(--text-main) !important;
}

.btn-reset {
  background-color: var(--bg-page) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.btn-reset:hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
  border-color: var(--border-hover) !important;
}

.btn-export {
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 600 !important;
}

.btn-import {
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 600 !important;
  background-color: var(--bg-page) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
}

.btn-import:hover {
  background-color: var(--bg-hover) !important;
  color: var(--text-main) !important;
  border-color: var(--border-hover) !important;
}

.btn-reload {
  border-radius: 8px !important;
  font-weight: 600 !important;
  height: 32px;
  padding: 0 16px;
  background-color: var(--bg-reload) !important;
  border-color: var(--border-reload) !important;
  color: var(--text-reload) !important;
}

.btn-reload:hover,
.btn-reload:focus {
  background-color: var(--bg-reload-hover) !important;
  border-color: var(--border-reload-hover) !important;
  color: var(--text-reload-hover) !important;
}

.search-form-inline {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 12px;
}

.search-form-inline :deep(.el-form-item) {
  margin-bottom: 0 !important;
  margin-right: 0 !important;
}

.filter-form-item :deep(.el-form-item__label) {
  display: none;
}

.filter-select {
  width: 160px;
  flex-shrink: 0;
}

.filter-select :deep(.el-input__inner) {
  border-radius: 8px;
  background-color: var(--bg-card);
  border-color: var(--border-color);
  color: var(--text-main);
  font-weight: 500;
  height: 32px;
  line-height: 32px;
  transition: all 0.2s ease;
}

.filter-select :deep(.el-input__inner:hover) {
  border-color: var(--border-hover);
}

.filter-select :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.server-search-input {
  width: 160px;
  flex-shrink: 0;
}

.server-search-input :deep(.el-input__inner),
.search-input :deep(.el-input__inner) {
  border-radius: 8px;
  border-color: var(--border-color);
  height: 32px;
  line-height: 32px;
  font-weight: 500;
  background-color: var(--bg-card);
  color: var(--text-main);
  transition: all 0.2s ease;
}

.server-search-input :deep(.el-input__inner:hover),
.search-input :deep(.el-input__inner:hover) {
  border-color: var(--border-hover);
}

.server-search-input :deep(.el-input__inner:focus),
.search-input :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.1);
}

.search-input {
  width: 220px;
  flex-shrink: 0;
}

.btn-setting {
  flex-shrink: 0;
  background-color: var(--bg-card) !important;
  border-color: var(--border-color) !important;
  color: var(--text-muted) !important;
  height: 32px;
  padding: 0 16px;
  border-radius: 8px !important;
  font-weight: 500 !important;
}

.btn-setting:hover {
  background-color: var(--bg-page) !important;
  border-color: var(--border-hover);
  color: var(--text-main) !important;
}

.table-card {
  padding: 20px;
  border-radius: var(--border-radius-card);
  background-color: var(--bg-card);
  border: 1px solid var(--border-color);
  box-shadow: var(--shadow-base);
}

.premium-table {
  border-radius: 8px;
  overflow: hidden;
  border: none !important;
  font-size: 13px !important;
  color: var(--text-main) !important;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", Arial, "PingFang SC", "Hiragino Sans GB", "Microsoft YaHei", sans-serif !important;
}

.premium-table::before {
  display: none !important;
}

.premium-table :deep(.el-table__header-wrapper) th,
.premium-table :deep(.el-table__fixed-header-wrapper) th,
.premium-table :deep(.el-table__fixed-right .el-table__fixed-header-wrapper) th {
  background-color: var(--bg-page) !important;
  color: var(--text-muted) !important;
  font-weight: 700 !important;
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-color) !important;
}

.premium-table :deep(.el-table__row) td {
  padding: 12px 0 !important;
  border-bottom: 1px solid var(--border-light) !important;
}

.premium-table :deep(.el-table__row) {
  transition: background-color 0.2s ease;
}

.premium-table :deep(.el-table__row:hover) td,
.premium-table :deep(.el-table__body tr.hover-row > td),
.premium-table :deep(.el-table__body tr.hover-row.current-row > td),
.premium-table :deep(.el-table__body tr.hover-row.el-table__row--striped > td) {
  background-color: var(--bg-hover) !important;
}

/* 操作列三点图标 */
.action-btn {
  font-size: 12px;
  border: 1px solid var(--border-color);
  color: var(--text-muted);
  background: var(--bg-card);
  transition: all 0.2s ease;
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.action-btn:hover {
  background: var(--bg-hover);
  color: var(--text-main);
  border-color: var(--border-hover);
}

/* 分页器 */
.pagination-container {
  display: flex;
  justify-content: flex-end;
  margin-top: 20px;
}

.premium-pagination :deep(.btn-prev),
.premium-pagination :deep(.btn-next),
.premium-pagination :deep(.el-pager li) {
  background-color: var(--bg-hover) !important;
  color: var(--text-muted) !important;
  border-radius: 6px !important;
  font-weight: 600 !important;
  margin: 0 3px !important;
}

.premium-pagination :deep(.el-pager li.active) {
  background-color: var(--primary-color) !important;
  color: #FFFFFF !important;
}

.premium-pagination :deep(.el-pagination__sizes .el-input__inner),
.premium-pagination :deep(.el-pagination__editor.el-input .el-input__inner) {
  border-radius: 6px;
  border-color: var(--border-color);
}

/* 列配置 Popover 内部样式 */
.popover-title {
  font-size: 13.5px;
  font-weight: 700;
  color: var(--text-main);
  padding: 6px 4px;
}

.popover-divider {
  height: 1px;
  background: var(--border-color);
  margin: 6px 0;
}

.popover-body-scroll {
  max-height: 250px;
  overflow-y: auto;
  padding: 4px;
}

.popover-body-scroll :deep(.el-checkbox) {
  display: block;
  margin-bottom: 8px;
  margin-right: 0;
}

.popover-body-scroll::-webkit-scrollbar {
  width: 4px;
}

.popover-body-scroll::-webkit-scrollbar-thumb {
  background: var(--border-hover);
  border-radius: 2px;
}

/* 弹窗样式 */
.premium-dialog :deep(.el-dialog) {
  border-radius: 12px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.1), 0 10px 10px -5px rgba(0, 0, 0, 0.04) !important;
}

.btn-save {
  background-color: var(--primary-color) !important;
  border-color: var(--primary-color) !important;
  font-weight: 600 !important;
}

.btn-save:hover {
  background-color: var(--primary-hover) !important;
  border-color: var(--primary-hover) !important;
}

.rest-premium-form {
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
}

.rest-premium-form :deep(.el-form-item) {
  width: calc(50% - 8px);
  margin-bottom: 0 !important;
}

.rest-premium-form :deep(.el-form-item.is-full-width) {
  width: 100%;
}

.rest-premium-form :deep(.el-form-item__label) {
  font-weight: 600;
  color: var(--text-main);
  padding-bottom: 6px !important;
}

.rest-premium-form :deep(.el-input__inner) {
  border-radius: 6px;
  border-color: var(--border-color);
}

.rest-premium-form :deep(.el-input__inner:focus) {
  border-color: var(--primary-color);
}

:deep(.rest-form-drawer) {
  background: var(--bg-page);
}

:deep(.rest-form-drawer .el-drawer__body) {
  height: 100%;
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.drawer-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-shrink: 0;
  padding: 14px 20px;
  border-bottom: 1px solid var(--border-color);
  background: var(--bg-card);
}

.drawer-title-wrap {
  min-width: 0;
  width: 100%;
  display: flex;
  align-items: center;
  gap: 10px;
}

.drawer-header-actions {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.drawer-header-actions .el-button + .el-button {
  margin-left: 0;
}

.drawer-title-icon {
  width: 28px;
  height: 28px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  border-radius: 8px;
  background: rgba(37, 99, 235, 0.08);
  color: var(--primary-color);
}

.drawer-title {
  overflow: hidden;
  color: var(--text-main);
  font-size: 15px;
  font-weight: 700;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.drawer-close-btn {
  display: none !important;
  width: 32px !important;
  height: 32px !important;
  padding: 0 !important;
  border-color: var(--border-color) !important;
  border-radius: 8px !important;
  color: var(--text-muted) !important;
}

.drawer-content {
  flex: 1;
  overflow-y: auto;
  margin: 12px;
  padding: 20px;
  border: 1px solid var(--border-color);
  border-radius: 8px;
  background: var(--bg-card);
}

.drawer-horizontal-form {
  display: block;
}

.drawer-horizontal-form :deep(.el-form-item),
.drawer-horizontal-form :deep(.el-form-item.is-full-width) {
  width: 100%;
  display: flex;
  align-items: flex-start;
  margin-bottom: 18px !important;
}

.drawer-horizontal-form :deep(.el-form-item__label) {
  width: 150px !important;
  flex: 0 0 150px;
  padding: 0 16px 0 0 !important;
  color: var(--text-muted);
  font-weight: 600;
  line-height: 32px;
  text-align: left;
}

.drawer-horizontal-form :deep(.el-form-item__content) {
  min-width: 0;
  flex: 1;
  margin-left: 0 !important;
}

.drawer-horizontal-form :deep(.el-form-item.is-label-hidden .el-form-item__label) {
  display: none;
}

.drawer-horizontal-form :deep(.el-input__inner),
.drawer-horizontal-form :deep(.el-textarea__inner) {
  border-color: var(--border-color);
  border-radius: 8px;
}

.drawer-horizontal-form :deep(.el-input__inner:focus),
.drawer-horizontal-form :deep(.el-textarea__inner:focus) {
  border-color: var(--primary-color);
  box-shadow: 0 0 0 2px rgba(37, 99, 235, 0.08);
}

.drawer-footer {
  display: none;
  align-items: center;
  justify-content: flex-end;
  gap: 12px;
  flex-shrink: 0;
  padding: 14px 20px;
  border-top: 1px solid var(--border-color);
  background: var(--bg-card);
}

.drawer-footer .el-button + .el-button {
  margin-left: 0;
}

@media (max-width: 768px) {
  .rest-workspace {
    padding: 12px;
  }

  .drawer-content {
    margin: 8px;
    padding: 16px;
  }

  .drawer-horizontal-form :deep(.el-form-item),
  .drawer-horizontal-form :deep(.el-form-item.is-full-width) {
    display: block;
  }

  .drawer-horizontal-form :deep(.el-form-item__label) {
    width: 100% !important;
    padding-right: 0 !important;
  }
}

/* Rest 组件暗色细节精调 */
html.dark .drawer-title-icon {
  background: rgba(59, 130, 246, 0.18) !important;
  color: #60a5fa !important;
}
html.dark .rest-premium-form :deep(.el-form-item__label) {
  color: var(--text-main) !important;
}
html.dark .drawer-horizontal-form :deep(.el-form-item__label) {
  color: var(--text-muted) !important;
}
html.dark .rest-premium-form :deep(.el-input__inner:focus),
html.dark .drawer-horizontal-form :deep(.el-input__inner:focus),
html.dark .drawer-horizontal-form :deep(.el-textarea__inner:focus) {
  box-shadow: 0 0 0 2px rgba(59, 130, 246, 0.25) !important;
}
</style>
